/**
 * OAuth 제공자 인터페이스
 */
import { config } from '../config/environment';

export interface IOAuthStrategy {
  initialize(): void;
  login(): Promise<void>;
  destroy?(): void;
}

export enum OAuthProvider {
  GOOGLE = 'google',
  KAKAO = 'kakao',
  NAVER = 'naver',
}

/**
 * Google OAuth 전략
 */
export class GoogleOAuthStrategy implements IOAuthStrategy {
  private clientId: string;
  private redirectUri: string;
  private state: string;

  constructor() {
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    // ⭐ 백엔드 CLIENT_URL과 정확히 일치하도록 설정
    // 배포: https://herbot.kwonyda.com/oauth/google/callback
    // 로컬: http://localhost:3000/oauth/google/callback
    const apiUrl = config.apiUrl;
    
    // 디버깅용 로그 (상세)
    console.group('[GoogleOAuthStrategy] Constructor Debug');
    console.log('config.apiUrl:', apiUrl);
    console.log('window.location.origin:', window.location.origin);
    console.log('process.env.NODE_ENV:', process.env.NODE_ENV);
    console.log('REACT_APP_API_URL env:', process.env.REACT_APP_API_URL);
    
    const clientUrl = apiUrl.includes('herbotapi.kwonyda.com') 
      ? 'https://herbot.kwonyda.com'
      : apiUrl.includes('localhost') 
      ? 'http://localhost:3000'
      : window.location.origin;
      
    this.redirectUri = `${clientUrl}/oauth/google/callback`;
    this.state = this.generateState();
    
    console.log('Determined clientUrl:', clientUrl);
    console.log('Final redirectUri:', this.redirectUri);
    console.log('clientId exists:', !!this.clientId);
    console.groupEnd();
    
    console.log('[GoogleOAuthStrategy] Initialized with:', {
      clientId: this.clientId ? this.clientId.substring(0, 20) + '...' : '✗',
      redirectUri: this.redirectUri
    });
    
    if (!this.clientId) {
      console.error('[GoogleOAuthStrategy] ❌ Google Client ID is not configured!');
      console.error('Make sure REACT_APP_GOOGLE_CLIENT_ID is set in environment variables');
    }
  }

  initialize(): void {
    // 초기화 로직 제거 - Authorization Code Flow 사용으로 SDK 렌더링 불필요
  }

  private generateState(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  async login(): Promise<void> {
    try {
      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.append('client_id', this.clientId);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('scope', 'openid email profile');
      authUrl.searchParams.append('redirect_uri', this.redirectUri);
      authUrl.searchParams.append('state', this.state);
      authUrl.searchParams.append('access_type', 'offline');
      authUrl.searchParams.append('prompt', 'login');

      // 상세 디버깅 로그
      console.group('[GoogleOAuthStrategy] About to redirect to Google');
      console.log('Full Google OAuth URL:', authUrl.toString());
      console.log('Parameters:');
      console.log('  - client_id:', this.clientId ? this.clientId.substring(0, 20) + '...' : '❌ MISSING');
      console.log('  - response_type:', 'code');
      console.log('  - redirect_uri:', this.redirectUri, '⭐ CRITICAL - must match Google Console');
      console.log('  - scope:', 'openid email profile');
      console.log('  - state:', this.state);
      console.groupEnd();

      // state를 세션 스토리지에 저장
      sessionStorage.setItem('google_oauth_state', this.state);

      console.log('[GoogleOAuthStrategy] Stored state in sessionStorage');

      // Google 로그인 페이지로 리다이렉트
      console.log('[GoogleOAuthStrategy] ➡️ Redirecting to Google...');
      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('[GoogleOAuth] ❌ Login failed:', error);
      throw error;
    }
  }

  destroy(): void {
    sessionStorage.removeItem('google_oauth_state');
  }
}

declare global {
  interface Window {
    google?: any;
    Kakao?: any;
  }
}

/**
 * Kakao OAuth 전략
 */
export class KakaoOAuthStrategy implements IOAuthStrategy {
  private clientId: string;
  private redirectUri: string;
  private initialized = false;

  constructor() {
    this.clientId = process.env.REACT_APP_KAKAO_CLIENT_ID || '';
    this.redirectUri = `${window.location.origin}/auth/kakao/callback`;
  }

  initialize(): void {
    if (this.initialized) return;

    const script = document.createElement('script');
    script.src = 'https://developers.kakao.com/sdk/js/kakao.min.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.Kakao && !window.Kakao.isInitialized()) {
        window.Kakao.init(this.clientId);
      }
      this.initialized = true;
    };
  }

  async login(): Promise<void> {
    if (window.Kakao) {
      window.Kakao.Auth.loginForm({
        success: (authObj: any) => {
          this.handleLoginSuccess(authObj);
        },
        fail: (err: any) => {
          console.error('Kakao login failed:', err);
        },
      });
    }
  }

  private async handleLoginSuccess(authObj: any): Promise<void> {
    try {
      // Kakao 사용자 정보 조회
      if (window.Kakao?.API?.request) {
        window.Kakao.API.request({
          url: '/v2/user/me',
          success: async (response: any) => {
            try {
              const userData = {
                id: String(response.id),
                email: response.kakao_account?.email || '',
                name: response.kakao_account?.profile?.nickname || 'Kakao User',
                picture: response.kakao_account?.profile?.profile_image_url,
                provider: 'kakao',
              };
              
              // DB에 사용자 정보 저장 (save-user 반드시 성공해야 함)
              await this.saveUserToDatabase(userData);
              
              localStorage.setItem('user', JSON.stringify(userData));
              window.location.reload();
            } catch (error) {
              console.error('[KakaoOAuth] Login failed during save:', error);
              // save-user 실패 시 로그인 실패
              window.Kakao.Auth.logout();
            }
          },
          fail: (err: any) => {
            console.error('[KakaoOAuth] User info request failed:', err);
          },
        });
      }
    } catch (error) {
      console.error('Kakao login processing failed:', error);
      throw error;
    }
  }

  private saveUserToDatabase = async (userData: any) => {
    try {
      const apiUrl = config.apiUrl;
      const response = await fetch(`${apiUrl}/auth/save-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[KakaoOAuth] Save user failed:', response.status, errorText);
        throw new Error(`Failed to save user: ${response.status}`);
      }

      const result = await response.json();
      return result.user || result;
    } catch (error) {
      console.error('[KakaoOAuth] Error saving user to database:', error);
      throw error;
    }
  };

  destroy(): void {
    if (window.Kakao) {
      window.Kakao.Auth.logout();
    }
  }
}

/**
 * Naver OAuth 전략
 */
export class NaverOAuthStrategy implements IOAuthStrategy {
  private clientId: string;
  private redirectUri: string;
  private state: string;
  private initialized = false;

  constructor() {
    this.clientId = process.env.REACT_APP_NAVER_CLIENT_ID || '';
    this.redirectUri = `${window.location.origin}/auth/naver/callback`;
    this.state = this.generateState();
  }

  initialize(): void {
    if (this.initialized) {
      this.initialized = true;
    }
  }

  async login(): Promise<void> {
    const authUrl = new URL('https://nid.naver.com/oauth2.0/authorize');
    authUrl.searchParams.append('client_id', this.clientId);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('redirect_uri', this.redirectUri);
    authUrl.searchParams.append('state', this.state);

    // state를 세션 스토리지에 저장
    sessionStorage.setItem('naver_oauth_state', this.state);

    window.location.href = authUrl.toString();
  }

  private generateState(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  destroy(): void {
    sessionStorage.removeItem('naver_oauth_state');
  }
}

/**
 * OAuth 제공자 팩토리
 */
export class OAuthStrategyFactory {
  static create(provider: OAuthProvider): IOAuthStrategy {
    switch (provider) {
      case OAuthProvider.GOOGLE:
        return new GoogleOAuthStrategy();
      case OAuthProvider.KAKAO:
        return new KakaoOAuthStrategy();
      case OAuthProvider.NAVER:
        return new NaverOAuthStrategy();
      default:
        throw new Error(`Unknown OAuth provider: ${provider}`);
    }
  }
}
